import { libFind } from './constants.jsx';

export const uid = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
export const snapVal = (v, snap, on) => (on ? Math.round(v / snap) * snap : v);
export const deepClone = (o) => JSON.parse(JSON.stringify(o));

// ─── CONTAINER TYPES ─────────────────────────────────────────────────────────
// These types hold children directly inside themselves (true parent-child).
export const CONTAINER_TYPES = new Set([
  'grid_container', 'flex_row', 'flex_col', 'columns', 'section', 'free_div',
]);
export const isContainer = (type) => CONTAINER_TYPES.has(type);

// ─── CHILD-CAPABLE LEAF TYPES ─────────────────────────────────────────────────
// Types that can be dropped into a container as children.
export const CHILD_TYPES = new Set([
  'fb_input', 'fb_dropdown', 'fb_date', 'fb_textarea', 'fb_file',
  'search_bar', 'btn_primary', 'btn_secondary', 'btn_danger',
  'text_block', 'alert_box', 'badge', 'stat_card', 'divider',
  'image_block', 'empty_state', 'data_table', 'chart_bar', 'chart_line',
  'chart_pie', 'rich_html',
]);
export const isChild = (type) => CHILD_TYPES.has(type);

// ─── CHILD DEFAULT PROPS ──────────────────────────────────────────────────────
// Children have no x/y/w/h — layout is owned by the parent container.
// They only carry their own visual/content props.
export function makeChildDefault(type) {
  const id = uid();
  const base = { id, type, _isChild: true };

  const fb = (extra = {}) => ({
    ...base,
    labelText: libFind(type).label,
    placeholder: 'Enter value…',
    showLabel: true, isMandatory: false,
    labelFontSize: 13, labelFontWeight: 500, labelColor: '#111827',
    helpText: '',
    inputBg: '#ffffff', inputBorderWidth: 1, inputBorderStyle: 'solid',
    inputBorderColor: '#CDD0D8', inputBorderRadius: 7,
    inputPaddingV: 10, inputPaddingH: 12,
    inputFontSize: 14, inputColor: '#111827',
    placeholderColor: '#9CA3AF', gap: 6,
    ...extra,
  });

  const CHILD_DEFAULTS = {
    fb_input:      fb({ inputType: 'text' }),
    fb_dropdown:   fb({ placeholder: 'Select…', src: 'static', staticOpts: 'Option A\nOption B\nOption C', isMulti: false }),
    fb_date:       fb({ placeholder: '', dateType: 'day' }),
    fb_textarea:   fb({ placeholder: 'Enter text…', rows: 3 }),
    fb_file:       fb({ uploadPath: '', downloadPath: '' }),
    search_bar:    { ...base, placeholder: 'Search…', bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#CDD0D8', borderRadius: 8, fontSize: 14, iconColor: '#9CA3AF', textColor: '#9CA3AF', padH: 12, padV: 8 },
    btn_primary:   { ...base, label: 'Confirm', bg: '#2563EB', color: '#ffffff', borderWidth: 0, borderStyle: 'none', borderColor: 'transparent', borderRadius: 7, fontSize: 14, fontWeight: 600, padH: 16 },
    btn_secondary: { ...base, label: 'Cancel', bg: '#ffffff', color: '#111827', borderWidth: 1, borderStyle: 'solid', borderColor: '#CDD0D8', borderRadius: 7, fontSize: 14, fontWeight: 500, padH: 16 },
    btn_danger:    { ...base, label: 'Delete', bg: '#DC2626', color: '#ffffff', borderWidth: 0, borderStyle: 'none', borderColor: 'transparent', borderRadius: 7, fontSize: 14, fontWeight: 600, padH: 16 },
    text_block:    { ...base, content: 'Text here', variant: 'body', align: 'left', color: '#111827', fontSize: 14, fontWeight: 400, lineHeight: 1.6 },
    alert_box:     { ...base, variant: 'info', title: 'Info', message: 'Message here.', showIcon: true, borderRadius: 8, padV: 10, padH: 14, titleFontSize: 13, msgFontSize: 12 },
    badge:         { ...base, text: 'Active', variant: 'success', fontSz: 12, fontW: 600, padH: 10, padV: 3, radius: 20, customBg: '', customColor: '' },
    stat_card:     { ...base, label: 'Metric', value: '—', trend: '', trendDir: 'up', bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 8, padT: 14, padR: 14, padB: 10, padL: 14, labelFontSize: 11, labelColor: '#6B7280', labelFontWeight: 500, valueFontSize: 22, valueColor: '#111827', valueFontWeight: 700, trendFontSize: 11, trendFontWeight: 600, trendUpColor: '#059669', trendDownColor: '#DC2626', showShadow: false },
    divider:       { ...base, label: '', lineColor: '#E2E5EA', lineHeight: 1, lineStyle: 'solid', labelFontSize: 12, labelColor: '#9CA3AF', labelBg: '#F7F8FA', paddingH: 8 },
    image_block:   { ...base, src: '', alt: 'Image', caption: '', objectFit: 'cover', borderRadius: 8, showCaption: false, capFontSize: 11, capColor: '#6B7280', capAlign: 'center', capBg: '#fff' },
    empty_state:   { ...base, icon: '◌', title: 'No data', message: 'Nothing here yet.', action: '', bg: '#fff', borderEnabled: true, borderColor: '#CDD0D8', borderStyle: 'dashed', borderWidth: 1, borderRadius: 8, iconSize: 28, iconColor: '#9CA3AF', titleFontSize: 13, titleColor: '#111827', msgFontSize: 12, msgColor: '#6B7280', actionBg: '#2563EB', actionColor: '#fff', actionRadius: 6 },
    data_table:    { ...base, title: 'Table', bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 8, showShadow: false, columns: [{ field: 'id', label: 'ID' }, { field: 'name', label: 'Name' }], striped: true, stripedColor: '#F7F8FA', searchable: false, pagination: false, pageSize: 5, headerBg: '#F7F8FA', headerColor: '#6B7280', headerFontSize: 11, rowFontSize: 12, rowColor: '#111827', cellPadV: 7, cellPadH: 10 },
    chart_bar:     { ...base, title: 'Chart', bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 8, barColor: '#2563EB', barRadius: 3, showGrid: true, gridColor: '#F0F2F5', titleFontSize: 12, titleColor: '#111827' },
    chart_line:    { ...base, title: 'Chart', bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 8, lineColor: '#059669', lineWidth: 2, fillOpacity: 0.08, showPoints: true, smooth: true, showGrid: true, gridColor: '#F0F2F5', titleFontSize: 12, titleColor: '#111827' },
    chart_pie:     { ...base, title: 'Chart', bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 8, donut: true, donutRatio: 0.5, titleFontSize: 12, titleColor: '#111827', slices: [{ label: 'A', value: 50, color: '#2563EB' }, { label: 'B', value: 50, color: '#059669' }] },
    rich_html:     { ...base, html: '<p>HTML here</p>', padding: 10 },
  };

  return CHILD_DEFAULTS[type] || base;
}

// ─── ROOT COMPONENT DEFAULT PROPS ─────────────────────────────────────────────
// Root components sit on the canvas with x/y/w/h and carry a children[] array
// if they are containers.
export function makeDefault(type) {
  const id = uid();
  const base = { id, type, x: 40, y: 40 };

  const fbBase = (extra = {}) => ({
    ...base,
    w: 300, h: 90,
    labelText: libFind(type).label,
    placeholder: 'Enter value…',
    showLabel: true, isMandatory: false,
    labelFontSize: 13, labelFontWeight: 500, labelColor: '#111827',
    helpText: '',
    inputBg: '#ffffff', inputBorderWidth: 1, inputBorderStyle: 'solid',
    inputBorderColor: '#CDD0D8', inputBorderRadius: 7,
    inputPaddingV: 10, inputPaddingH: 12,
    inputFontSize: 14, inputColor: '#111827',
    placeholderColor: '#9CA3AF', gap: 6,
    ...extra,
  });

  const containerBase = (extra = {}) => ({
    ...base,
    // children[] is the source of truth for what's inside the container
    children: [],
    // grid/flex layout props
    gap: 12,
    padT: 12, padR: 12, padB: 12, padL: 12,
    bg: 'transparent',
    borderWidth: 1, borderStyle: 'dashed', borderColor: '#CBD5E1', borderRadius: 8,
    showCellOutlines: true,
    ...extra,
  });

  const DEFAULTS = {
    // ── Layout (containers) ──────────────────────────────────────────────────
    page_header: {
      ...base, w: 900, h: 70, titleAlign: 'left',
      title: 'Page Title', subtitle: 'Describe this page',
      titleFontSize: 22, titleFontWeight: 700, titleColor: '#111827',
      titleMaxWidth: '', titleWrap: false, titleLineClamp: 1, titleLineHeight: 1.2, titleLetterSpacing: 0,
      titleItalic: false, titleUnderline: false, titleTransform: 'none',
      showSubtitle: true,
      subtitleAlign: 'left', subtitleMaxWidth: '', subtitleWrap: true, subtitleLineClamp: 2,
      subtitleFontSize: 14, subtitleFontWeight: 400, subtitleColor: '#6B7280', subtitleLineHeight: 1.4, subtitleLetterSpacing: 0,
      subtitleItalic: false, subtitleUnderline: false, subtitleTransform: 'none',
      actionGap: 6, actionInnerGap: 5, actionPadH: '', actionPadV: '', actionRadius: '',
      showIcon: false, iconType: 'emoji', iconEmoji: '📋', iconUrl: '',
      iconSize: 32, iconBg: 'transparent', iconRadius: 8, titleAreaGap: 10,
      bg: 'transparent', borderBottom: true, borderColor: '#E2E5EA',
      paddingV: 16, paddingH: 0,
      actions: [{ label: 'Export', variant: 'secondary' }, { label: 'New Record', variant: 'primary' }],
    },
    section: {
      ...base, w: 860, h: 'auto',
      title: 'Section Title', showTitle: true,
      bg: '#ffffff', borderEnabled: true, borderWidth: 1, borderStyle: 'solid',
      borderColor: '#E2E5EA', borderRadius: 10, shadow: 'sm',
      padT: 20, padR: 20, padB: 20, padL: 20,
      titleFontSize: 15, titleFontWeight: 600, titleColor: '#111827',
      showDivider: true, dividerColor: '#E2E5EA',
      // children[] holds child components inside this section
      children: [],
      childLayout: 'block', // 'block' | 'flex' | 'grid'
      childCols: 2,         // used when childLayout = 'grid'
      childGap: 16,
    },
    flex_row: containerBase({
      w: 700, h: 80,
      flexDir: 'row', alignItems: 'center', justifyContent: 'flex-start', flexWrap: false,
    }),
    flex_col: containerBase({
      w: 320, h: 'auto',
      flexDir: 'column', alignItems: 'stretch', justifyContent: 'flex-start',
    }),
    grid_container: containerBase({
      w: 860, h: 'auto',
      gridCols: 3, gridRows: 'auto',
      colTemplate: '1fr 1fr 1fr', rowTemplate: 'auto',
      bg: 'transparent', borderStyle: 'dashed',
    }),
    free_div: {
      ...base, w: 400, h: 200,
      bg: '#ffffff', borderWidth: 1, borderStyle: 'solid',
      borderColor: '#E2E5EA', borderRadius: 8,
      padT: 16, padR: 16, padB: 16, padL: 16,
      children: [],
      childLayout: 'block', childCols: 1, childGap: 12,
    },
    columns: {
      ...base, w: 860, h: 'auto',
      numCols: 2, colGap: 20,
      padT: 0, padR: 0, padB: 0, padL: 0,
      bg: 'transparent', borderWidth: 0, borderStyle: 'none', borderColor: '#E2E5EA',
      // columns stores per-column config; children are split across columns
      columnDefs: [
        { id: uid(), bg: 'transparent', borderColor: '#CDD0D8', borderRadius: 6, padding: 8, children: [] },
        { id: uid(), bg: 'transparent', borderColor: '#CDD0D8', borderRadius: 6, padding: 8, children: [] },
      ],
    },
    divider: {
      ...base, w: 860, h: 32, label: '',
      lineColor: '#E2E5EA', lineHeight: 1, lineStyle: 'solid',
      labelFontSize: 12, labelColor: '#9CA3AF', labelBg: '#F7F8FA', paddingH: 12,
    },

    // ── Data Display ────────────────────────────────────────────────────────
    stat_card: {
      ...base, w: 240, h: 130,
      label: 'Total Revenue', value: '₹2,40,000', trend: '+12.4%', trendDir: 'up',
      bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 10,
      padT: 20, padR: 20, padB: 16, padL: 20,
      labelFontSize: 12, labelColor: '#6B7280', labelFontWeight: 500,
      valueFontSize: 28, valueColor: '#111827', valueFontWeight: 700,
      trendFontSize: 12, trendFontWeight: 600, trendUpColor: '#059669', trendDownColor: '#DC2626',
      showShadow: true,
    },
    data_table: {
      ...base, w: 860, h: 400, title: 'Data Table',
      bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 10,
      showShadow: true,
      columns: [{ field: 'id', label: 'ID' }, { field: 'name', label: 'Name' }, { field: 'status', label: 'Status' }],
      striped: true, stripedColor: '#F7F8FA',
      searchable: true, pagination: true, pageSize: 10,
      headerBg: '#F7F8FA', headerColor: '#6B7280', headerFontSize: 11,
      rowFontSize: 13, rowColor: '#111827', cellPadV: 10, cellPadH: 16,
    },
    chart_bar: {
      ...base, w: 520, h: 300, title: 'Bar Chart',
      bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 10,
      barColor: '#2563EB', barRadius: 4, showGrid: true, gridColor: '#F0F2F5',
      titleFontSize: 14, titleColor: '#111827',
    },
    chart_line: {
      ...base, w: 520, h: 300, title: 'Line Chart',
      bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 10,
      lineColor: '#059669', lineWidth: 2.5, fillOpacity: 0.08, showPoints: true, smooth: true,
      showGrid: true, gridColor: '#F0F2F5', titleFontSize: 14, titleColor: '#111827',
    },
    chart_pie: {
      ...base, w: 360, h: 300, title: 'Distribution',
      bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#E2E5EA', borderRadius: 10,
      donut: true, donutRatio: 0.5, titleFontSize: 14, titleColor: '#111827',
      slices: [
        { label: 'A', value: 35, color: '#2563EB' }, { label: 'B', value: 25, color: '#059669' },
        { label: 'C', value: 20, color: '#D97706' }, { label: 'D', value: 20, color: '#9CA3AF' },
      ],
    },
    badge: {
      ...base, w: 100, h: 32, text: 'Active', variant: 'success',
      fontSz: 12, fontW: 600, padH: 12, padV: 4, radius: 20, customBg: '', customColor: '',
    },

    // ── Forms ───────────────────────────────────────────────────────────────
    fb_input:    fbBase({ inputType: 'text', maxChars: '', maxVal: '' }),
    fb_dropdown: fbBase({ placeholder: 'Select…', src: 'static', staticOpts: 'Option A\nOption B\nOption C', isMulti: false }),
    fb_date:     fbBase({ placeholder: '', dateType: 'day' }),
    fb_textarea: fbBase({ placeholder: 'Enter text…', rows: 4, h: 130 }),
    fb_file:     fbBase({ uploadPath: '', downloadPath: '' }),
    search_bar: {
      ...base, w: 340, h: 44, placeholder: 'Search…',
      bg: '#ffffff', borderWidth: 1, borderStyle: 'solid', borderColor: '#CDD0D8', borderRadius: 8,
      fontSize: 14, iconColor: '#9CA3AF', textColor: '#9CA3AF', padH: 14, padV: 10,
    },

    // ── Actions ─────────────────────────────────────────────────────────────
    btn_primary:   { ...base, w: 140, h: 40, label: 'Confirm', bg: '#2563EB', color: '#ffffff', borderWidth: 0, borderStyle: 'none', borderColor: 'transparent', borderRadius: 7, fontSize: 14, fontWeight: 600, padH: 20 },
    btn_secondary: { ...base, w: 140, h: 40, label: 'Cancel',  bg: '#ffffff', color: '#111827', borderWidth: 1, borderStyle: 'solid', borderColor: '#CDD0D8', borderRadius: 7, fontSize: 14, fontWeight: 500, padH: 20 },
    btn_danger:    { ...base, w: 140, h: 40, label: 'Delete',  bg: '#DC2626', color: '#ffffff', borderWidth: 0, borderStyle: 'none', borderColor: 'transparent', borderRadius: 7, fontSize: 14, fontWeight: 600, padH: 20 },

    // ── Content ─────────────────────────────────────────────────────────────
    text_block:  { ...base, w: 600, h: 50, content: 'Enter your text here.', variant: 'body', align: 'left', color: '#111827', fontSize: 14, fontWeight: 400, lineHeight: 1.6, italic: false, underline: false, letterSpacing: 0 },
    alert_box:   { ...base, w: 860, h: 72, variant: 'info', title: 'Information', message: 'This is an informational message.', showIcon: true, borderRadius: 8, padV: 13, padH: 15, titleFontSize: 14, msgFontSize: 13 },
    empty_state: { ...base, w: 860, h: 280, icon: '◌', title: 'No data yet', message: 'Items will appear here once added.', action: 'Get Started', bg: '#ffffff', borderEnabled: true, borderColor: '#CDD0D8', borderStyle: 'dashed', borderWidth: 1, borderRadius: 10, iconSize: 40, iconColor: '#9CA3AF', titleFontSize: 16, titleColor: '#111827', msgFontSize: 13, msgColor: '#6B7280', actionBg: '#2563EB', actionColor: '#fff', actionRadius: 7 },
    image_block: { ...base, w: 400, h: 240, src: '', alt: 'Image', caption: '', objectFit: 'cover', borderRadius: 10, showCaption: true, capFontSize: 12, capColor: '#6B7280', capAlign: 'center', capBg: '#ffffff' },
    rich_html:   { ...base, w: 600, h: 200, html: '<p>Custom HTML content here</p>', padding: 16 },
  };

  return DEFAULTS[type] || { ...base, w: 200, h: 80 };
}
